<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

import { cn, ICON_STROKE, Tooltip } from '@/shared/ui'
import { useAuthStore } from '@/stores/auth'
import { useBrandingStore } from '@/stores/branding'
import { useUiStore } from '@/stores/ui'

import { NAVIGATION } from './navigation'

/**
 * Fixný ľavý sidebar: 248 px, zbalený 64 px (tokeny `--sidebar-w`).
 *
 * Zbalený stav ukazuje len ikony a názov sa presúva do tooltipu — inak by sa
 * po zbalení stratila orientácia. Aktívna položka je jedno z mála miest, kde
 * sa smie objaviť brand farba.
 */
const ui = useUiStore()
const auth = useAuthStore()
const branding = useBrandingStore()

const groups = computed(() =>
	NAVIGATION.map((group) => ({
		...group,
		items: group.items.filter((item) => auth.hasAnyRole(item.roles ?? [])),
	})).filter((group) => group.items.length > 0),
)
</script>

<template>
	<aside
		:class="
			cn(
				'fixed inset-y-0 left-0 z-(--z-drawer) flex flex-col border-r border-border bg-surface',
				// Šírka sa prepína skokom: animácia `width` je v MASTER.md §5
				// zakázaná (rozhýbe layout celej stránky, nie len sidebaru).
				ui.sidebarCollapsed ? 'w-(--sidebar-w-collapsed)' : 'w-(--sidebar-w)',
			)
		"
		aria-label="Hlavná navigácia"
	>
		<!-- Logo max 32 px s bezpečnou zónou 8 px (MASTER.md §2); bez loga wordmark. -->
		<div class="flex h-(--topbar-h) items-center gap-2 border-b border-border px-2">
			<img
				v-if="branding.logoUrl !== null"
				:src="branding.logoUrl"
				:alt="branding.companyName"
				class="max-h-8 w-auto shrink-0"
			>
			<span
				v-else-if="ui.sidebarCollapsed"
				class="flex size-8 shrink-0 items-center justify-center rounded-md bg-brand text-xs font-semibold text-on-brand"
				aria-hidden="true"
			>
				{{ branding.initials }}
			</span>
			<span v-else class="flex min-w-0 flex-col">
				<span class="truncate text-[15px] leading-tight font-semibold text-fg">{{ branding.companyName }}</span>
				<span class="mt-0.5 h-[3px] w-8 rounded-sm bg-brand" aria-hidden="true" />
			</span>
		</div>

		<nav class="flex-1 overflow-y-auto p-2">
			<div v-for="(group, index) in groups" :key="group.labelSk ?? `group-${index}`" class="mb-4">
				<p
					v-if="group.labelSk !== null && !ui.sidebarCollapsed"
					class="px-2 pb-1 text-xs font-medium tracking-wide text-fg-subtle uppercase"
				>
					{{ group.labelSk }}
				</p>
				<ul class="flex flex-col gap-0.5">
					<li v-for="item in group.items" :key="item.routeName">
						<Tooltip :content-sk="item.labelSk" side="right" :delay-ms="ui.sidebarCollapsed ? 200 : 700">
							<RouterLink
								:to="{ name: item.routeName }"
								:class="
									cn(
										'flex h-(--control-h) items-center gap-2 rounded-md px-2 text-base text-fg-muted',
										'transition-colors duration-(--motion-fast) ease-out',
										'hover:bg-surface-muted hover:text-fg focus-visible:focus-ring',
										ui.sidebarCollapsed ? 'justify-center' : '',
									)
								"
								active-class="bg-brand-soft font-medium text-fg"
							>
								<component :is="item.icon" :size="20" :stroke-width="ICON_STROKE" class="shrink-0" aria-hidden="true" />
								<span v-if="!ui.sidebarCollapsed" class="truncate">{{ item.labelSk }}</span>
								<span v-else class="sr-only">{{ item.labelSk }}</span>
							</RouterLink>
						</Tooltip>
					</li>
				</ul>
			</div>
		</nav>
	</aside>
</template>
